import { binCentreStation, type SuperBin } from '@/lib/stationVolumes/aggregate';
import { BAR_RADIUS_PX, barPath, type crossfallPaths, TICK_FONT, tickM3 } from '@/lib/stationVolumes/chartLayout';
import { AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';

/** The linear scales the panes draw with — `@visx/scale`'s own return type. */
type LinearScale = ReturnType<typeof scaleLinear<number>>;

/** Axis tick size, shared so both panes and the station axis agree. */
export { TICK_FONT };

/**
 * Presentational pieces of `StationVolumeChart` — the legend row, the hover
 * tooltip card, the visually-hidden data table, and the chart's own CSS.
 *
 * They live here, not in the chart, so the chart file is the drawing and the
 * interaction and nothing else. Every one of them takes finished strings: the
 * chart owns `t`, these own markup.
 */

/**
 * Chart-local CSS. The palette itself lives in `SV_TOKENS` on the dock root;
 * these are the rules that need the chart's own element ids (the hatch
 * patterns) or its own motion.
 */
export const CHART_CSS = `
.sv-chart:focus-visible{outline:2px solid var(--sv-walk);outline-offset:2px;border-radius:4px;}
/* The brush handles are not separate tab stops (the chart is one tab stop,
   spec §4b), so their focus affordance is "the chart has focus and there is
   a brush" — Shift+arrows then move the trailing handle. */
.sv-chart:focus-visible .sv-handle{stroke-width:3;}
.sv-walk-cursor,.sv-hover-cursor{pointer-events:none;}
@media (prefers-reduced-motion:no-preference){
  .sv-walk-cursor{transition:transform 120ms cubic-bezier(.2,0,0,1);}
}
/* Forced colours: the tokens already remap to system colours; swap the solid
   fills for hatches so cut and fill stay separable when every hue collapses. */
@media (forced-colors:active){
  .sv-bar-fill{fill:url(#sv-hatch-fill);}
  .sv-bar-cut{fill:url(#sv-hatch-cut);}
  /* The baseline is the datum every bar is read against; in forced colours it
     is drawn at full strength, not at the 35 % that keeps it quiet on our own
     surface. */
  .sv-zero-line{stroke:CanvasText;stroke-opacity:1;}
}
`;

export const FLOATING_TOOLTIP_STYLE = { position: 'absolute', zIndex: 9999, pointerEvents: 'none' } as const;

/**
 * One tooltip row: label left, figure right, tabular so columns line up.
 *
 * A row may carry an `ink`, which colours its LABEL. That is for rows whose
 * series is identified by hue in the pane above — the crossfall pane's two
 * sides — so the reader can match the figure to the line without a legend
 * lookup. The figure itself stays in the card's own ink: colouring the number
 * would make the value look like a status.
 */
export const TooltipCard = ({
  station,
  rows,
}: {
  station: string;
  rows: { label: string; value: string; ink?: string }[];
}) => (
  <div className='min-w-40'>
    <div className='sv-sta mb-1 text-[11px] opacity-90'>{station}</div>
    <div className='sv-num flex flex-col gap-0.5'>
      {rows.map((r) => (
        <div key={r.label} className='flex items-baseline justify-between gap-4'>
          <span className={r.ink ? '' : 'opacity-75'} style={r.ink ? { color: r.ink } : undefined}>
            {r.label}
          </span>
          <span>{r.value}</span>
        </div>
      ))}
    </div>
  </div>
);

/**
 * The visually-hidden data table that satisfies "a table view exists" without
 * a second UI (spec §5 A11y point 4). It carries what is *drawn*: when the
 * bins are super-binned the Table tab is the full-resolution view, and the
 * caption says so.
 */
export const HiddenDataTable = ({
  caption,
  headers,
  rows,
  currentRow,
}: {
  caption: string;
  headers: string[];
  rows: { key: number; cells: string[] }[];
  currentRow: number | null;
}) => (
  <table className='sr-only'>
    <caption>{caption}</caption>
    <thead>
      <tr>
        {headers.map((h) => (
          <th key={h} scope='col'>
            {h}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.key} aria-current={r.key === currentRow ? 'location' : undefined}>
          <th scope='row'>{r.cells[0]}</th>
          {r.cells.slice(1).map((c, i) => (
            <td key={headers[i + 1]}>{c}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * The legend is always present — cut and fill have to be identifiable without
 * hovering anything — and it carries the two honesty chips beside it: the
 * hatch key when any bin is under-covered, and the super-bin factor when
 * neighbouring bins have been summed.
 */
export const ChartLegend = ({
  fillLabel,
  cutLabel,
  cutConvention,
  fillConvention,
  lowCoverageLabel,
  lowCoverageHelp,
  groupedLabel,
  groupedHelp,
  hint,
  massHaul,
  lowerPane,
  crossfallLegend,
}: {
  fillLabel: string;
  cutLabel: string;
  /**
   * The cut/fill CONVENTION in words (T4, closing F21) — the short form for
   * the swatch row, the full sentence as its tooltip.
   *
   * `cutLabel` is not this. "Cut, below the line" describes the DRAWING; it
   * says nothing about which way round the ground is, and a reader who has
   * just come from a per-station PDF needs to know that cut is material
   * sitting above the design. Two facts, two labels.
   */
  cutConvention: { short: string; full: string };
  fillConvention: { short: string; full: string };
  /** `null` when no bin in view is under-covered. */
  lowCoverageLabel: string | null;
  lowCoverageHelp: string;
  /** `null` unless the chart is drawing super-bins. */
  groupedLabel: string | null;
  groupedHelp: string;
  hint: string;
  /** `null` outside compact mode, where both panes are shown at once. */
  massHaul: { label: string; help: string; on: boolean; onToggle: () => void } | null;
  /**
   * The crossfall pane's four marks, shown only while that pane is on.
   *
   * It needs a VISIBLE key, unlike the other two panes: it is the only pane
   * that draws four lines whose sole difference is hue, and an SVG `<title>`
   * on a 2 px stroke needs the reader to find and hover that stroke first.
   * The two side swatches carry the pane's own inks; design and tolerance are
   * drawn in `currentColor`, because each of those exists in BOTH inks and a
   * swatch in one of them would claim otherwise.
   */
  crossfallLegend: { left: string; right: string; design: string; tolerance: string } | null;
  /**
   * What the lower pane plots. `null` on a run with no true station-line
   * areas — there is nothing to switch to, so no control is offered rather
   * than a disabled one nobody can act on.
   */
  lowerPane: {
    value: 'haul' | 'endArea' | 'crossfall';
    onChange: (v: 'haul' | 'endArea' | 'crossfall') => void;
    haulLabel: string;
    endAreaLabel: string;
    /** Absent on a run with no crossfall check: the option is then not
     *  offered at all rather than offered disabled. */
    crossfallLabel?: string;
    /** Per-option explanations. "End area" is the term readers most often
     *  mistake for a volume, so each option carries its own sentence rather
     *  than sharing one on the group. */
    haulHelp: string;
    endAreaHelp: string;
    crossfallHelp?: string;
    groupLabel: string;
    help: string;
  } | null;
}) => (
  <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600 dark:text-stone-400'>
    <span className='flex items-center gap-1'>
      <svg width={12} height={12} aria-hidden>
        <rect x={1} y={1} width={10} height={5} fill='var(--sv-fill)' />
      </svg>
      {fillLabel}
      <span className='opacity-70' data-testid='sv-legend-convention' title={fillConvention.full}>
        {fillConvention.short}
      </span>
    </span>
    <span className='flex items-center gap-1'>
      <svg width={12} height={12} aria-hidden>
        <rect x={1} y={6} width={10} height={5} fill='var(--sv-cut)' />
      </svg>
      {cutLabel}
      <span className='opacity-70' data-testid='sv-legend-convention' title={cutConvention.full}>
        {cutConvention.short}
      </span>
    </span>
    {lowCoverageLabel && (
      <span className='flex items-center gap-1' title={lowCoverageHelp}>
        <svg width={12} height={12} aria-hidden>
          <rect x={1} y={1} width={10} height={10} fill='currentColor' fillOpacity={0.15} />
          <path d='M1,11 L11,1 M1,6 L6,1 M6,11 L11,6' stroke='currentColor' strokeWidth={1} fill='none' />
        </svg>
        {lowCoverageLabel}
      </span>
    )}
    {crossfallLegend && (
      <span className='flex items-center gap-x-3 gap-y-1' data-testid='sv-crossfall-legend'>
        <span className='flex items-center gap-1'>
          <svg width={12} height={12} aria-hidden>
            <line x1={0} y1={6} x2={12} y2={6} stroke='var(--sv-xf-left)' strokeWidth={2} />
          </svg>
          {crossfallLegend.left}
        </span>
        <span className='flex items-center gap-1'>
          <svg width={12} height={12} aria-hidden>
            <line x1={0} y1={6} x2={12} y2={6} stroke='var(--sv-xf-right)' strokeWidth={2} />
          </svg>
          {crossfallLegend.right}
        </span>
        <span className='flex items-center gap-1'>
          <svg width={12} height={12} aria-hidden>
            <line
              x1={0}
              y1={6}
              x2={12}
              y2={6}
              stroke='currentColor'
              strokeWidth={1.5}
              strokeDasharray='4,2'
              opacity={0.8}
            />
          </svg>
          {crossfallLegend.design}
        </span>
        <span className='flex items-center gap-1'>
          <svg width={12} height={12} aria-hidden>
            <rect x={0} y={2} width={12} height={8} fill='currentColor' fillOpacity={0.18} />
          </svg>
          {crossfallLegend.tolerance}
        </span>
      </span>
    )}
    {groupedLabel && (
      <span className='sv-chip sv-num' data-testid='sv-grouped' title={groupedHelp}>
        {groupedLabel}
      </span>
    )}
    <span className='ml-auto flex items-center gap-2'>
      {lowerPane && (
        // A segmented control, not two independent toggles: the lower pane
        // shows exactly one of these, and a pair of checkboxes would imply a
        // state (both, or neither) that does not exist.
        <span
          role='radiogroup'
          aria-label={lowerPane.groupLabel}
          title={lowerPane.help}
          data-testid='sv-lowerpane-group'
          className='flex items-center overflow-hidden rounded border border-slate-300 dark:border-stone-600'
        >
          {(
            [
              ['haul', lowerPane.haulLabel, lowerPane.haulHelp],
              ['endArea', lowerPane.endAreaLabel, lowerPane.endAreaHelp],
              ...(lowerPane.crossfallLabel
                ? ([['crossfall', lowerPane.crossfallLabel, lowerPane.crossfallHelp ?? '']] as const)
                : []),
            ] as const
          ).map(([value, label, optionHelp]) => {
            const on = lowerPane.value === value;
            return (
              <button
                key={value}
                type='button'
                role='radio'
                aria-checked={on}
                title={optionHelp}
                data-testid={`sv-lowerpane-${value}`}
                onClick={() => lowerPane.onChange(value)}
                className={`min-h-6 px-2 py-0.5 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none focus-visible:ring-inset ${
                  on ? 'bg-slate-200 font-semibold dark:bg-stone-700' : ''
                }`}
              >
                {label}
              </button>
            );
          })}
        </span>
      )}
      {massHaul && (
        <button
          type='button'
          data-testid='sv-masshaul-toggle'
          aria-pressed={massHaul.on}
          title={massHaul.help}
          onClick={massHaul.onToggle}
          className={`min-h-6 rounded border px-2 py-0.5 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none ${
            massHaul.on ? 'border-current font-semibold' : 'border-slate-300 dark:border-stone-600'
          }`}
        >
          {massHaul.label}
        </button>
      )}
      <span className='whitespace-nowrap'>{hint}</span>
    </span>
  </div>
);

/** Axis tick label props, shared by both panes and the station axis. */
export const tickLabelProps = (extra?: Record<string, unknown>) => () => ({
  fill: 'currentColor',
  fontSize: TICK_FONT,
  ...extra,
});

/**
 * The bar pane: cut and fill diverging around zero, fill above and cut below.
 *
 * The sign is a STRUCTURAL encoding, so cut and fill survive full
 * colour-blindness and greyscale print with no legend at all. Low-coverage
 * bins keep the same hue at 55 % with a 45° hatch and square caps — never
 * grey, because a grey bar reads as "zero" and these bins are "unknown",
 * which is a different and more dangerous thing (spec §4b).
 */
export const BarPane = ({
  bars,
  show,
  xScale,
  yScale,
  barW,
  paneHeight,
  innerWidth,
  marginLeft,
  selLo,
  selHi,
  hoverGroup,
  extremes,
  axisLabel,
  formatVolume,
}: {
  bars: SuperBin[];
  show: boolean;
  xScale: LinearScale;
  yScale: LinearScale;
  barW: number;
  paneHeight: number;
  innerWidth: number;
  marginLeft: number;
  /** Brushed range in ORIGINAL bin indices; `-1` when there is no brush. */
  selLo: number;
  selHi: number;
  hoverGroup: number | null;
  /** Indices into `bars` of the extreme cut and fill bars, or `-1`. */
  extremes: { cut: number; fill: number };
  axisLabel: string;
  /** The chart's dataset-wide m³ formatter (`m3Formatter(...).volume`), so a
   *  direct label and the tooltip over the same bar carry the same decimals. */
  formatVolume: (v: number) => string;
}) => {
  if (!show) return null;
  const zero = yScale(0);
  const hasSel = selLo >= 0;
  return (
    <Group>
      {yScale.ticks(4).map((tick) => (
        <line
          key={`gb-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      <AxisLeft
        scale={yScale}
        numTicks={4}
        stroke='currentColor'
        tickStroke='currentColor'
        tickFormat={(v) => tickM3(Number(v))}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
      </text>
      {/* The zero baseline is the datum the whole pane is read against, so it
          is drawn in ink: `currentColor` at 35 %, which is a step darker than
          the gridlines and clearly visible on both surfaces. `--sv-zero`
          measured 1.48 : 1 against the dark surface — invisible (ruling A15);
          it stays the neutral *fill* token (the 3D ramp's midpoint) and is no
          longer used as a stroke anywhere. 1.5 px rather than 1 px is what
          separates "the axis" from "a gridline". */}
      <line
        className='sv-zero-line'
        x1={0}
        x2={innerWidth}
        y1={zero}
        y2={zero}
        stroke='currentColor'
        strokeOpacity={0.35}
        strokeWidth={1.5}
      />

      {bars.map((b, i) => {
        const x = xScale(binCentreStation(b)) - barW / 2;
        const inSel = !hasSel || (b.toBin >= selLo && b.fromBin <= selHi);
        const fillH = Math.max(0, zero - yScale(b.fillVolumeM3));
        const cutH = Math.max(0, yScale(-b.cutVolumeM3) - zero);
        const round = b.lowCoverage ? 'none' : undefined;
        // Low coverage is a caveat, not a de-emphasis: 0.55 on this surface,
        // not the spec's 40 % (written for a light one — ruling A15), and
        // unselected bars 0.45 rather than 0.35. Hover brightens EVERY bar,
        // under-covered ones included: the old expression short-circuited on
        // `lowCoverage`, so hovering the one bar a reader is suspicious about
        // was the one hover that did nothing.
        const hovered = i === hoverGroup;
        const opacity = hovered ? (b.lowCoverage ? 0.85 : 1) : !inSel ? 0.45 : b.lowCoverage ? 0.55 : 1;
        return (
          <g key={b.i} opacity={opacity} data-testid={hovered ? 'sv-bar-hover' : undefined}>
            {b.fillVolumeM3 > 0 && (
              <path
                className='sv-bar-fill'
                d={barPath(x, yScale(b.fillVolumeM3), barW, fillH, BAR_RADIUS_PX, round ?? 'top')}
                fill='var(--sv-fill)'
              />
            )}
            {b.cutVolumeM3 > 0 && (
              <path
                className='sv-bar-cut'
                d={barPath(x, zero, barW, cutH, BAR_RADIUS_PX, round ?? 'bottom')}
                fill='var(--sv-cut)'
              />
            )}
            {b.lowCoverage && fillH + cutH > 0 && (
              <rect
                x={x}
                y={zero - fillH}
                width={barW}
                height={fillH + cutH}
                fill='url(#sv-hatch-lowcov)'
                pointerEvents='none'
              />
            )}
          </g>
        );
      })}

      {/* Direct labels on the extreme cut bar and the extreme fill bar only.
          A number on every bar is a table, badly drawn. */}
      {extremes.fill >= 0 && (
        <text
          data-testid='sv-label-fill'
          x={xScale(binCentreStation(bars[extremes.fill]))}
          y={Math.max(9, yScale(bars[extremes.fill].fillVolumeM3) - 4)}
          textAnchor='middle'
          fontSize={TICK_FONT}
          className='sv-num'
          fill='var(--sv-fill)'
        >
          {formatVolume(bars[extremes.fill].fillVolumeM3)}
        </text>
      )}
      {extremes.cut >= 0 && (
        <text
          data-testid='sv-label-cut'
          x={xScale(binCentreStation(bars[extremes.cut]))}
          y={Math.min(paneHeight - 1, yScale(-bars[extremes.cut].cutVolumeM3) + 10)}
          textAnchor='middle'
          fontSize={TICK_FONT}
          className='sv-num'
          fill='var(--sv-cut)'
        >
          {/* Negative, because the axis this bar hangs from is: `tickM3`
              keeps the sign, so a bare magnitude here would read as a
              contradiction against the −100 tick beside it. Matches the PDF
              chart (`processReport/stationChart.ts`). */}
          {formatVolume(-bars[extremes.cut].cutVolumeM3)}
        </text>
      )}
    </Group>
  );
};

/**
 * The mass-haul pane: cumulative net volume, with a diamond on every zero
 * crossing. `--sv-haul` is neutral ink rather than a series hue, because the
 * curve is a derived quantity and not a third category (spec §4b).
 */
export const HaulPane = ({
  show,
  top,
  path,
  xScale,
  yScale,
  innerWidth,
  marginLeft,
  balance,
  balanceLabels,
  axisLabel,
}: {
  show: boolean;
  top: number;
  path: string;
  xScale: LinearScale;
  yScale: LinearScale;
  innerWidth: number;
  marginLeft: number;
  /** Internal stations of the zero crossings. */
  balance: number[];
  /** Label per crossing, `null` where it would collide with its neighbour. */
  balanceLabels: (string | null)[];
  axisLabel: string;
}) => {
  if (!show) return null;
  const zero = yScale(0);
  return (
    <Group top={top}>
      {/* Same hairline grid as the bar pane, so the two read as one
          instrument rather than a chart and a sparkline. */}
      {yScale.ticks(3).map((tick) => (
        <line
          key={`gh-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      <AxisLeft
        scale={yScale}
        numTicks={3}
        stroke='currentColor'
        tickStroke='currentColor'
        tickFormat={(v) => tickM3(Number(v))}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
      </text>
      {/* Same datum, same ink as the bar pane's — the two panes have to read
          as one instrument. */}
      <line
        className='sv-zero-line'
        x1={0}
        x2={innerWidth}
        y1={zero}
        y2={zero}
        stroke='currentColor'
        strokeOpacity={0.35}
        strokeWidth={1.5}
      />
      <path
        data-testid='sv-haul-path'
        d={path}
        fill='none'
        stroke='var(--sv-haul)'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      {balance.map((s, i) => (
        <g key={`bp-${s}`}>
          <rect
            data-testid='sv-balance'
            x={xScale(s) - 5}
            y={zero - 5}
            width={10}
            height={10}
            transform={`rotate(45 ${xScale(s)} ${zero})`}
            fill='var(--sv-haul)'
            stroke='var(--sv-surface)'
            strokeWidth={2}
          />
          {balanceLabels[i] && (
            <text x={xScale(s) + 9} y={zero - 7} fontSize={TICK_FONT} className='sv-num' fill='currentColor'>
              {balanceLabels[i]}
            </text>
          )}
        </g>
      ))}
    </Group>
  );
};

/**
 * The end-area pane: cut and fill cross-section area in m² at every station
 * LINE — the answer to "what is the area between the surfaces at this
 * chainage", which the bar pane's m³ cannot give.
 *
 * A polyline per side rather than diverging bars, and a `[0, max]` domain
 * rather than one centred on zero: an area has no sign. Cut and fill are two
 * series here, not two directions, and they carry the same two hues the bars
 * and the section bands already use so the three views read as one instrument.
 */
export const EndAreaPane = ({
  show,
  top,
  cutPath,
  fillPath,
  yScale,
  innerWidth,
  marginLeft,
  axisLabel,
  axisHelp,
}: {
  show: boolean;
  top: number;
  cutPath: string;
  fillPath: string;
  yScale: LinearScale;
  innerWidth: number;
  marginLeft: number;
  axisLabel: string;
  /** Rendered as an SVG <title>, so hovering the axis label explains the
   *  quantity to a reader who never touches the pane control. */
  axisHelp?: string;
}) => {
  if (!show) return null;
  return (
    <Group top={top}>
      {yScale.ticks(3).map((tick) => (
        <line
          key={`ge-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      <AxisLeft
        scale={yScale}
        numTicks={3}
        stroke='currentColor'
        tickStroke='currentColor'
        tickFormat={(v) => String(Math.round(Number(v)))}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
        {axisHelp && <title>{axisHelp}</title>}
      </text>
      {/* Zero is the floor here, not a datum the series straddles. */}
      <line
        className='sv-zero-line'
        x1={0}
        x2={innerWidth}
        y1={yScale(0)}
        y2={yScale(0)}
        stroke='currentColor'
        strokeOpacity={0.35}
        strokeWidth={1.5}
      />
      <path
        data-testid='sv-endarea-fill-path'
        d={fillPath}
        fill='none'
        stroke='var(--sv-fill)'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        data-testid='sv-endarea-cut-path'
        d={cutPath}
        fill='none'
        stroke='var(--sv-cut)'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </Group>
  );
};

/**
 * The crossfall pane: as-built left/right solid, design left/right dashed,
 * the tolerance band shaded around each design line.
 *
 * Its own pane and its own scale for the same reason the end-area pane has
 * one: percent and m³ share no datum, and on a dual-scale axis the crossing
 * of two series is an artefact of the scales.
 *
 * The two inks are `--sv-xf-left` / `--sv-xf-right`, NOT the cut/fill pair:
 * left and right are two sides of the road, a category, and the diverging
 * pair means "either side of zero" everywhere else in this dock.
 */
export const CrossfallPane = ({
  show,
  top,
  paths,
  yScale,
  innerWidth,
  marginLeft,
  axisLabel,
  axisHelp,
  leftLabel,
  rightLabel,
  designLabel,
  toleranceLabel,
}: {
  show: boolean;
  top: number;
  paths: ReturnType<typeof crossfallPaths>;
  yScale: LinearScale;
  innerWidth: number;
  marginLeft: number;
  axisLabel: string;
  /** Rendered as an SVG <title>, so hovering the axis label explains the
   *  quantity to a reader who never touches the pane control. */
  axisHelp?: string;
  /** Named on every mark as an SVG <title>: with four lines in two hues, the
   *  identity of each one has to survive a reader who never hovers the
   *  tooltip and a reader who cannot see the two hues apart. */
  leftLabel: string;
  rightLabel: string;
  designLabel: string;
  toleranceLabel: string;
}) => {
  if (!show) return null;
  const line = (d: string, ink: string, testId: string, dashed: boolean, name: string) =>
    d ? (
      <path
        data-testid={testId}
        d={d}
        fill='none'
        stroke={ink}
        strokeWidth={dashed ? 1.5 : 2}
        strokeDasharray={dashed ? '5,3' : undefined}
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <title>{name}</title>
      </path>
    ) : null;
  return (
    <Group top={top}>
      {yScale.ticks(3).map((tick) => (
        <line
          key={`gx-${tick}`}
          x1={0}
          x2={innerWidth}
          y1={yScale(tick)}
          y2={yScale(tick)}
          stroke='var(--sv-grid)'
          pointerEvents='none'
        />
      ))}
      <AxisLeft
        scale={yScale}
        numTicks={3}
        stroke='currentColor'
        tickStroke='currentColor'
        tickFormat={(v) => Number(v).toFixed(1)}
        tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
      />
      <text x={-marginLeft + 2} y={-2} fontSize={TICK_FONT} fill='currentColor' opacity={0.7}>
        {axisLabel}
        {axisHelp && <title>{axisHelp}</title>}
      </text>
      {/* The tolerance first, under everything: it is the ground the two
          series are read against, never a mark on top of them. */}
      {paths.toleranceLeft && (
        <path
          data-testid='sv-crossfall-tolerance-left'
          d={paths.toleranceLeft}
          fill='var(--sv-xf-left)'
          fillOpacity={0.12}
          stroke='none'
        >
          <title>{`${toleranceLabel} · ${leftLabel}`}</title>
        </path>
      )}
      {paths.toleranceRight && (
        <path
          data-testid='sv-crossfall-tolerance-right'
          d={paths.toleranceRight}
          fill='var(--sv-xf-right)'
          fillOpacity={0.12}
          stroke='none'
        >
          <title>{`${toleranceLabel} · ${rightLabel}`}</title>
        </path>
      )}
      {line(paths.designLeft, 'var(--sv-xf-left)', 'sv-crossfall-design-left-path', true, `${designLabel} · ${leftLabel}`)}
      {line(
        paths.designRight,
        'var(--sv-xf-right)',
        'sv-crossfall-design-right-path',
        true,
        `${designLabel} · ${rightLabel}`,
      )}
      {line(paths.asbuiltLeft, 'var(--sv-xf-left)', 'sv-crossfall-asbuilt-left-path', false, leftLabel)}
      {line(paths.asbuiltRight, 'var(--sv-xf-right)', 'sv-crossfall-asbuilt-right-path', false, rightLabel)}
    </Group>
  );
};
